import { useQuery } from "@hooks/useQuery";
import { Icon } from "@iconify/react";
import { Button, Tabs } from "antd";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { Dine } from "./Dine/Dine";
import { LandingPageHeading } from "./LandingPage/LandingPage";
import ShopAndDineLablesData from "./LabelsData/LabelsData";

export const ShopAndDine = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const [currentAirport] = useLocalStorage("currentAirport", null);

  const isKlia = currentAirport === "klia1" || currentAirport === "klia2";

  useEffect(() => {
    const activeTab = query.get("activeTab");
    const subActiveTab = query.get("subActiveTab");
    const thirdSubActiveTab = query.get("thirdSubActiveTab");

    if (query.get("id")) {
      return;
    }

    if (!activeTab) {
      navigate(`/dedicated/passenger/shopAndDine/?activeTab=landingPageHeading`, {
        replace: true,
      });
    } else {
      if (activeTab === "landingPage") {
        navigate(`/dedicated/passenger/shopAndDine/?activeTab=landingPageHeading`, { replace: true });
      }
      if (activeTab === "shopAndDineLabelsData") {
        navigate(
          `/dedicated/passenger/shopAndDine?activeTab=shopAndDineLabelsData`,
          { replace: true }
        );
      }

      if (activeTab === "shop") {
        const defaultSubActiveTab = isKlia ? "shopCategories" : "shopList";

        // Mapping subActiveTab to its default thirdSubActiveTab
        const defaultThirdSubActiveTabMap: Record<string, string> = {
          shopCategories: "shopCategoriesPageHeading",
          shopList: isKlia ? "shopListPageContent" : "shopListPageHeading",
        };

        const effectiveSubActiveTab = subActiveTab ?? defaultSubActiveTab;

        const defaultThirdSubActiveTab = defaultThirdSubActiveTabMap[effectiveSubActiveTab] || "";

        navigate(
          `/dedicated/passenger/shopAndDine?activeTab=shop&subActiveTab=${effectiveSubActiveTab}${
            defaultThirdSubActiveTab ? `&thirdSubActiveTab=${thirdSubActiveTab || defaultThirdSubActiveTab}` : ""
          }`,
          { replace: true }
        );
      }

      if (activeTab === "dine") {
        const defaultSubActiveTab = isKlia ? "dineCategories" : "dineList";

        const effectiveSubActiveTab = subActiveTab ?? defaultSubActiveTab;

        const defaultThirdSubActiveTabMap: Record<string, string> = {
          dineCategories: "dineCategoriesPageHeading",
          dineList: isKlia ? "dineListPageContent" : "dineListPageHeading",
        };

        const defaultThirdSubActiveTab = defaultThirdSubActiveTabMap[effectiveSubActiveTab] || "";

        navigate(
          `/dedicated/passenger/shopAndDine?activeTab=dine&subActiveTab=${effectiveSubActiveTab}${
            defaultThirdSubActiveTab ? `&thirdSubActiveTab=${thirdSubActiveTab || defaultThirdSubActiveTab}` : ""
          }`,
          { replace: true }
        );
      }

      if (activeTab === "services") {
        const defaultSubActiveTab = isKlia ? "servicesCategories" : "servicesList";

        const effectiveSubActiveTab = subActiveTab ?? defaultSubActiveTab;

        const defaultThirdSubActiveTabMap: Record<string, string> = {
          servicesCategories: "servicesCategoriesPageHeading",
          servicesList: isKlia ? "servicesListPageContent" : "servicesListPageHeading",
        };

        const defaultThirdSubActiveTab = defaultThirdSubActiveTabMap[effectiveSubActiveTab] || "";

        navigate(
          `/dedicated/passenger/shopAndDine?activeTab=services&subActiveTab=${effectiveSubActiveTab}${
            defaultThirdSubActiveTab ? `&thirdSubActiveTab=${thirdSubActiveTab || defaultThirdSubActiveTab}` : ""
          }`,
          { replace: true }
        );
      }

      if (activeTab === "zone") {
        const defaultSubActiveTab = isKlia ? "zonePublic" : "zoneLevels";

        const effectiveSubActiveTab = subActiveTab ?? defaultSubActiveTab;

        navigate(`/dedicated/passenger/shopAndDine?activeTab=zone&subActiveTab=${effectiveSubActiveTab}`, {
          replace: true,
        });
      }
    }
  }, [query.get("activeTab"), query.get("subActiveTab"), query.get("thirdSubActiveTab")]);

  const tabItems = [
    {
      label: "Landing Page",
      key: "landingPageHeading",
      children: <LandingPageHeading />,
    },
    {
      label: "Labels Data",
      key: "shopAndDineLabelsData",
      children: <ShopAndDineLablesData />,
    },
  ];

  const handleShopDine = (subActiveTab: any, thirdSubActiveTab: any) => {
    if (subActiveTab === "landingPageContent") {
      return (
        <Button
          onClick={() => {
            navigate("/dedicated/passenger/shopAndDine/landingPage/add");
          }}
          icon={<Icon icon={"lucide:plus"} />}
        >
          Add Page Content
        </Button>
      );
    }
    if (subActiveTab === "zonePublic") {
      return (
        <Button
          onClick={() => {
            navigate("/dedicated/passenger/shopAndDine/zonePublic/add");
          }}
          icon={<Icon icon={"lucide:plus"} />}
        >
          Add Zone Public
        </Button>
      );
    }
    if (subActiveTab === "zonePassenger") {
      return (
        <Button
          onClick={() => {
            navigate("/dedicated/passenger/shopAndDine/zonePassenger/add");
          }}
          icon={<Icon icon={"lucide:plus"} />}
        >
          Add Zone Passenger
        </Button>
      );
    }
    if (subActiveTab === "zoneLevels") {
      return (
        <Button
          onClick={() => {
            navigate("/dedicated/passenger/shopAndDine/zoneLevels/add");
          }}
          icon={<Icon icon={"lucide:plus"} />}
        >
          Add Zone Level
        </Button>
      );
    }
    if (thirdSubActiveTab === "shopListPageContent") {
      return (
        <Button
          onClick={() => {
            navigate("/dedicated/passenger/shopAndDine/shop/shopList/shopListPageContent/add");
          }}
          icon={<Icon icon={"lucide:plus"} />}
        >
          Add List Content
        </Button>
      );
    }

    if (thirdSubActiveTab === "dineListPageContent") {
      return (
        <Button
          onClick={() => {
            navigate("/dedicated/passenger/shopAndDine/dine/dineList/dineListPageContent/add");
          }}
          icon={<Icon icon={"lucide:plus"} />}
        >
          Add List Content
        </Button>
      );
    }

    if (thirdSubActiveTab === "servicesListPageContent") {
      return (
        <Button
          onClick={() => {
            navigate("/dedicated/passenger/shopAndDine/services/servicesList/servicesListPageContent/add");
          }}
          icon={<Icon icon={"lucide:plus"} />}
        >
          Add List Content
        </Button>
      );
    }
  };

  return (
    <ContentWrapper
      title="Shop & Dine"
      bodyStyle={{ height: "calc(100vh - 100px)", overflowY: "auto" }}
      actionItems={handleShopDine(query.get("subActiveTab"), query.get("thirdSubActiveTab"))}
      airportText={`Airport Specific Content`}
    >
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={tabItems}
        onChange={(activeKey) => {
          navigate(`${window.location.pathname}?activeTab=${activeKey}`);
        }}
      />
    </ContentWrapper>
  );
};
